/**
 * Holds the app behind the device's own authentication.
 *
 * Two shapes, deliberately different:
 *
 * - **Cold start** — the children are not mounted at all. Nothing renders, no
 *   query fires, no tenant data is fetched, let alone painted. An overlay alone
 *   would leave the app running underneath it, which satisfies "no data
 *   renders" only in the visual sense.
 * - **Return from the background** — the children stay mounted and the lock
 *   sits over them. Unmounting a live session to re-lock would throw away every
 *   half-filled form on screen and force a full refetch, and the person has
 *   already been authenticated once in this process.
 *
 * The provider itself sits OUTSIDE `AuthProvider` and does not consume it: in
 * cold-start mode `AuthProvider` has not mounted yet, so there is no session to
 * ask. Everything it needs is synchronous and storage-backed.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { AUTH_STORAGE_KEYS } from '../auth/storageKeys';
import {
  clearRefreshToken,
  lockRefreshToken,
  unlockRefreshToken,
} from '../auth/secureTokenStore';
import { clearPersistedQueryCache } from '../offline/queryPersister';
import { AppLockScreen, type AppLockScreenStatus } from './AppLockScreen';
import {
  MAX_UNLOCK_FAILURES,
  clearAppLockState,
  clearBackgroundedAt,
  isAppLockSupportedPlatform,
  markProcessStarted,
  noteAppBackgrounded,
  readCachedPolicy,
  recordUnlockFailure,
  requiresFullLoginInsteadOfLock,
  resetUnlockFailures,
  setLockedForCrossTabSync,
  shouldLockOnBoot,
  shouldLockOnForeground,
} from './appLockState';
import { checkBiometricCapability, promptUnlock } from './biometricGate';

type LockMode =
  /** Locked before anything mounted — children are withheld. */
  | 'boot'
  /** Locked over a running session — children stay mounted underneath. */
  | 'resume'
  | null;

export function AppLockProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation(['auth']);
  const queryClient = useQueryClient();

  // Computed in the initialiser, so the very first paint is already correct.
  // A useEffect here would render the app for one frame before locking it.
  const [mode, setMode] = useState<LockMode>(() => (shouldLockOnBoot() ? 'boot' : null));
  const [status, setStatus] = useState<AppLockScreenStatus>({ kind: 'locked' });
  const promptInFlight = useRef(false);

  useEffect(() => {
    markProcessStarted();
    setLockedForCrossTabSync(mode !== null);
    if (mode !== null) {
      // The lock engaging — not the app merely backgrounding — is what puts the
      // refresh token back out of reach. Tying it to the LOCK means a short trip
      // to another app that stays under the idle threshold never disturbs a
      // silent refresh, while a real lock forces the next unlock to re-read the
      // keystore, which is how an enrolment change gets noticed at all.
      lockRefreshToken();
    }
  }, [mode]);

  /**
   * Destroy the session on this device and go back to the login page.
   *
   * Used by the explicit Sign out, by the failure lockout, and by a `required`
   * workspace on a handset that cannot lock. `purgeCache` is the difference
   * between "log in again" and "and take the cached company data with you".
   */
  const endSession = useCallback(
    async (purgeCache: boolean) => {
      localStorage.removeItem(AUTH_STORAGE_KEYS.accessToken);
      localStorage.removeItem(AUTH_STORAGE_KEYS.user);
      await clearRefreshToken();
      clearAppLockState();
      if (purgeCache) {
        // A device that failed five genuine attempts is more likely to be in
        // the wrong hands than to belong to someone who fumbled. The offline
        // snapshot of the tenant's orders and contacts goes with the session.
        await clearPersistedQueryCache(queryClient).catch(() => undefined);
      }
      window.location.href = '/login';
    },
    [queryClient]
  );

  const runUnlock = useCallback(async () => {
    if (promptInFlight.current) return;
    promptInFlight.current = true;
    setStatus({ kind: 'prompting' });
    try {
      // Everything below talks to the device — the biometric prompt, the
      // keystore — and this screen is the app's ONLY way back in, so anything
      // that escapes here leaves the person on a lock screen stuck at
      // `prompting` with no prompt coming and no button to press. Each callee
      // is contracted to answer with a value rather than throw; this is the
      // backstop for the one that ever stops honouring that.
      const capability = await checkBiometricCapability();

      if (requiresFullLoginInsteadOfLock(readCachedPolicy(), capability.canAuthenticate)) {
        // The workspace requires the lock and this handset cannot provide one.
        // Rather than let it through unlocked, it keeps no session at all.
        await endSession(false);
        return;
      }
      if (!capability.canAuthenticate) {
        setStatus({ kind: 'unavailable' });
        return;
      }

      const outcome = await promptUnlock({
        reason: t('auth:appLock.prompt.reason'),
        cancelTitle: t('auth:appLock.prompt.cancel'),
        androidTitle: t('auth:appLock.prompt.androidTitle'),
        androidSubtitle: t('auth:appLock.prompt.androidSubtitle'),
      });

      if (outcome.kind === 'unlocked') {
        // The device proved identity; now collect the token that proof unlocks.
        // In plain mode — every build shipped so far — this resolves `ok`
        // without touching the keystore, so the branch below is inert.
        const stored = await unlockRefreshToken();

        if (stored.kind === 'absent' || stored.kind === 'invalidated') {
          // The enrolled biometric set changed, so the OS destroyed the key that
          // wrapped the refresh token. That is the whole point of binding it:
          // whoever added their own face or finger gets a login page, not this
          // person's session. `purgeCache: false` — this is a re-authentication,
          // not the five-failures lockout, and the offline data survives it.
          await endSession(false);
          return;
        }
        if (stored.kind === 'unavailable') {
          // A keystore that could not answer is a device fault, not a verdict on
          // the session. Stay locked and let them try again rather than sign
          // them out of their offline data over a Keychain that was busy.
          setStatus({ kind: 'unavailable' });
          return;
        }

        resetUnlockFailures();
        clearBackgroundedAt();
        setStatus({ kind: 'locked' });
        setMode(null);
        return;
      }
      if (outcome.kind === 'cancelled') {
        // Not a failure. Nothing is counted and nothing is destroyed.
        setStatus({ kind: 'cancelled' });
        return;
      }

      const { failures, exhausted } = recordUnlockFailure();
      if (exhausted) {
        await endSession(true);
        return;
      }
      setStatus({ kind: 'failed', remaining: MAX_UNLOCK_FAILURES - failures });
    } catch (error) {
      // A device that could not answer is not a verdict on the session: stay
      // locked and let them try again, exactly as the `unavailable` outcome does.
      console.error('[applock] Unlock attempt failed unexpectedly:', error);
      setStatus({ kind: 'unavailable' });
    } finally {
      promptInFlight.current = false;
    }
  }, [endSession, t]);

  // Offer the prompt automatically whenever the lock appears, so reopening the
  // app is one glance rather than a tap and then a glance.
  useEffect(() => {
    if (mode === null) return;
    if (status.kind !== 'locked') return;
    void runUnlock();
  }, [mode, status.kind, runUnlock]);

  // Foreground / background. The lock STATE is raised here, but the prompt is
  // never fired from this handler: authenticating while the app is inactive
  // resolves as `appCancel` immediately and drops the person on a screen that
  // looks like it already refused them. Raising the state re-runs the effect
  // above once the app is actually active.
  useEffect(() => {
    if (!isAppLockSupportedPlatform()) return;
    let disposed = false;
    let remove: (() => void) | undefined;

    void import('@capacitor/app').then(({ App }) => {
      if (disposed) return;
      void App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) {
          noteAppBackgrounded();
          return;
        }
        if (shouldLockOnForeground()) {
          setStatus({ kind: 'locked' });
          setMode((current) => current ?? 'resume');
        } else {
          clearBackgroundedAt();
        }
      }).then((handle) => {
        if (disposed) {
          void handle.remove();
          return;
        }
        remove = () => void handle.remove();
      });
    });

    return () => {
      disposed = true;
      remove?.();
    };
  }, []);

  const handleSignOut = useCallback(() => {
    void endSession(false);
  }, [endSession]);

  const handleUnlock = useCallback(() => {
    void runUnlock();
  }, [runUnlock]);

  if (mode === 'boot') {
    return <AppLockScreen status={status} onUnlock={handleUnlock} onSignOut={handleSignOut} />;
  }

  return (
    <>
      {children}
      {mode === 'resume' && (
        <AppLockScreen status={status} onUnlock={handleUnlock} onSignOut={handleSignOut} />
      )}
    </>
  );
}
